import { translate } from '../../i18n/index.js';
import { useEffect, useRef, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { adminApi } from '../../api/admin.api.js';
import { settingsApi } from '../../api/settings.api.js';
import { useAuth } from '../../hooks/useAuth.js';
import type { NotificationTemplate, NotificationType } from '@atomfit/shared';
import { AdminMarketingCampaigns } from './AdminMarketingCampaigns.js';

const placeholders = [
  '{{gymName}}',
  '{{slotRange}}',
  '{{accessCode}}',
  '{{amountFormatted}}',
  '{{creditFormatted}}',
  '{{refundMessage}}',
  '{{context}}',
  '{{planName}}',
  '{{tenantName}}',
  '{{currentPeriodEnd}}',
  '{{membershipUrl}}',
  '{{cancellationMode}}',
  '{{accessUntil}}',
  '{{cardBrand}}',
  '{{cardLast4}}',
  '{{cardExpMonth}}',
  '{{cardExpYear}}',
  '{{email}}',
  '{{appUrl}}',
  '{{adminUrl}}',
  '{{roleLabel}}',
  '{{accessMessage}}',
  '{{magicLink}}',
  '{{resetLink}}',
  '{{verificationLink}}',
  '{{requestedAt}}',
  '{{expiresAt}}',
  '{{ttlMinutes}}',
];

const previewValues: Record<string, string> = {
  gymName: translate('pages.admin.admin_emails_page.preview_values.gym_name'),
  slotRange: '16. 5. 2026 18:00 - 19:00',
  accessCode: '482913',
  amountFormatted: '350.00 CZK',
  creditFormatted: translate('pages.admin.admin_emails_page.preview_values.credit_formatted'),
  refundMessage: translate('pages.admin.admin_emails_page.preview_values.refund_message'),
  context: translate('pages.admin.admin_emails_page.preview_values.context'),
  planName: 'Premium',
  tenantName: 'AtomFit',
  currentPeriodEnd: translate('pages.admin.admin_emails_page.preview_values.current_period_end'),
  membershipUrl: 'https://atomfit.cz/membership',
  cancellationMode: translate('pages.admin.admin_emails_page.preview_values.cancellation_mode'),
  accessUntil: translate('pages.admin.admin_emails_page.preview_values.access_until'),
  cardBrand: 'Visa',
  cardLast4: '4242',
  cardExpMonth: '06',
  cardExpYear: '2026',
  email: 'anna@example.com',
  appUrl: 'https://atomfit.cz',
  adminUrl: 'https://atomfit.cz/admin',
  roleLabel: 'OWNER_ADMIN',
  accessMessage: translate('pages.admin.admin_emails_page.preview_values.access_message'),
  magicLink: 'https://atomfit.cz/auth/verify?token=sample-token',
  resetLink: 'https://atomfit.cz/auth/reset-password?token=sample-token',
  verificationLink: 'https://atomfit.cz/auth/verify-email?token=sample-token',
  requestedAt: 'Dnes v 14:05',
  expiresAt: 'Dnes v 14:20',
  ttlMinutes: '15',
};

function renderPreview(template: string): string {
  return template.replace(
    /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
    (_match, key: string) => previewValues[key] ?? '',
  );
}

export function AdminEmailsPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [bookingNotificationChannel, setBookingNotificationChannel] = useState<
    'EMAIL' | 'SMS' | 'BOTH'
  >('BOTH');
  const [bookingReminderLeadTimeMinutes, setBookingReminderLeadTimeMinutes] = useState('60');
  const [bookingDayBeforeReminderEnabled, setBookingDayBeforeReminderEnabled] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<NotificationType>('BOOKING_CONFIRMATION');
  const [templateDraft, setTemplateDraft] = useState<NotificationTemplate | null>(null);
  const [showSource, setShowSource] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [settings, templateRes] = await Promise.all([
          settingsApi.get(),
          isPlatform
            ? adminApi.listNotificationTemplates()
            : Promise.resolve({ data: { data: [] as NotificationTemplate[] } }),
        ]);
        setBookingNotificationChannel(settings.data.bookingNotificationChannel);
        setBookingReminderLeadTimeMinutes(String(settings.data.bookingReminderLeadTimeMinutes));
        setBookingDayBeforeReminderEnabled(settings.data.bookingDayBeforeReminderEnabled ?? false);
        setTemplates(templateRes.data.data);
        const nextTemplate =
          templateRes.data.data.find((item) => item.type === selectedTemplateType) ??
          templateRes.data.data[0] ??
          null;
        setSelectedTemplateType(nextTemplate?.type ?? 'BOOKING_CONFIRMATION');
        setTemplateDraft(nextTemplate);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [isPlatform]);

  useEffect(() => {
    if (editorRef.current && templateDraft && !showSource) {
      editorRef.current.innerHTML = templateDraft.htmlBody;
    }
  }, [templateDraft?.type, showSource]);

  function patchDraft(patch: Partial<NotificationTemplate>) {
    setTemplateDraft((current) => (current ? { ...current, ...patch } : current));
  }

  function syncEditorHtml() {
    if (editorRef.current && templateDraft) {
      patchDraft({ htmlBody: editorRef.current.innerHTML });
    }
  }

  function runEditorCommand(command: string, value?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditorHtml();
  }

  function insertPlaceholder(value: string) {
    if (showSource) {
      patchDraft({ htmlBody: `${templateDraft?.htmlBody ?? ''}${value}` });
      return;
    }
    runEditorCommand('insertText', value);
  }

  async function saveSettings() {
    const reminderLeadTime = Number(bookingReminderLeadTimeMinutes);
    if (!Number.isInteger(reminderLeadTime) || reminderLeadTime < 5 || reminderLeadTime > 1440) {
      setMessage(translate('pages.admin.admin_emails_page.save_settings.set_message.time'));
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await settingsApi.update({
        bookingNotificationChannel,
        bookingReminderLeadTimeMinutes: reminderLeadTime,
        bookingDayBeforeReminderEnabled,
      });
      setMessage(translate('pages.admin.admin_emails_page.save_settings.set_message.save'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMessage(
        msg ?? translate('pages.admin.admin_emails_page.save_settings.set_message.error_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  async function saveTemplate() {
    if (!templateDraft) return;
    setSaving(true);
    setMessage(null);
    const htmlBody = showSource
      ? templateDraft.htmlBody
      : (editorRef.current?.innerHTML ?? templateDraft.htmlBody);
    try {
      const res = await adminApi.updateNotificationTemplate(templateDraft.type, {
        name: templateDraft.name,
        subject: templateDraft.subject,
        htmlBody,
        textBody: templateDraft.textBody,
        smsBody: templateDraft.smsBody,
        isEnabled: templateDraft.isEnabled,
      });
      setTemplates((items) => items.map((item) => (item.type === res.data.type ? res.data : item)));
      setTemplateDraft(res.data);
      setMessage(translate('pages.admin.admin_emails_page.save_template.set_message.save'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMessage(
        msg ?? translate('pages.admin.admin_emails_page.save_template.set_message.error_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  const previewHtml = templateDraft ? renderPreview(templateDraft.htmlBody) : '';
  const previewSubject = templateDraft ? renderPreview(templateDraft.subject) : '';
  const previewText = templateDraft ? renderPreview(templateDraft.textBody) : '';
  const previewSms = templateDraft?.smsBody ? renderPreview(templateDraft.smsBody) : '';

  return (
    <AdminLayout
      title={translate('pages.admin.admin_emails_page.admin_emails_page.admin_layout.email')}
      eyebrow={
        isPlatform
          ? translate('pages.admin.admin_emails_page.admin_emails_page.admin_layout.eyebrow')
          : translate('pages.admin.admin_emails_page.admin_emails_page.admin_layout.eyebrow_2')
      }
    >
      {loading ? (
        <div
          className="af-card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 24,
            color: 'var(--af-muted)',
          }}
        >
          <Spinner />{' '}
          {translate('pages.admin.admin_emails_page.admin_emails_page.div_card.loading_email')}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {isPlatform && <AdminMarketingCampaigns />}
          <section className="af-card" style={{ padding: 24 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 18,
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>
                  {translate('pages.admin.admin_emails_page.admin_emails_page.div.booking')}
                </div>
                <div style={{ color: 'var(--af-muted)', fontSize: 13, marginTop: 4 }}>
                  {translate(
                    'pages.admin.admin_emails_page.admin_emails_page.div.booking_confirmation',
                  )}
                </div>
              </div>
              <Tag>
                {bookingNotificationChannel === 'BOTH'
                  ? translate('pages.admin.admin_emails_page.admin_emails_page.tag.email_sms')
                  : bookingNotificationChannel}
              </Tag>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 320px) minmax(220px, 320px) auto',
                gap: 14,
                alignItems: 'end',
              }}
            >
              <label className="af-label">
                {translate('pages.admin.admin_emails_page.admin_emails_page.label_label.copy')}

                <CustomSelect
                  value={bookingNotificationChannel}
                  onChange={(value) =>
                    setBookingNotificationChannel(value as 'EMAIL' | 'SMS' | 'BOTH')
                  }
                  options={[
                    {
                      value: 'EMAIL',
                      label: translate(
                        'pages.admin.admin_emails_page.admin_emails_page.custom_select.options',
                      ),
                    },
                    {
                      value: 'SMS',
                      label: translate(
                        'pages.admin.admin_emails_page.admin_emails_page.custom_select.options_2',
                      ),
                    },
                    {
                      value: 'BOTH',
                      label: translate(
                        'pages.admin.admin_emails_page.admin_emails_page.custom_select.options_3',
                      ),
                    },
                  ]}
                />
              </label>
              <label className="af-label">
                {translate('pages.admin.admin_emails_page.admin_emails_page.label_label.time')}

                <input
                  className="af-input"
                  type="number"
                  min={5}
                  max={1440}
                  step={1}
                  value={bookingReminderLeadTimeMinutes}
                  onChange={(event) => setBookingReminderLeadTimeMinutes(event.target.value)}
                />
              </label>
              <button
                className="af-primary-btn"
                style={{ padding: '12px 18px', fontSize: 13, justifySelf: 'start' }}
                disabled={saving}
                onClick={() => void saveSettings()}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  translate(
                    'pages.admin.admin_emails_page.admin_emails_page.button_primary_btn.save',
                  )
                )}
              </button>
            </div>
            <label
              className="af-label"
              style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}
            >
              <input
                type="checkbox"
                checked={bookingDayBeforeReminderEnabled}
                onChange={(event) => setBookingDayBeforeReminderEnabled(event.target.checked)}
              />
              {translate('pages.admin.admin_emails_page.day_before_reminder')}
            </label>
          </section>

          {message && (
            <div
              className={message.includes('uloženo') ? '' : 'af-error'}
              style={
                message.includes('uloženo')
                  ? {
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: 'rgba(74,222,128,0.1)',
                      color: 'var(--af-success)',
                      fontSize: 13,
                    }
                  : undefined
              }
            >
              {message}
            </div>
          )}

          {isPlatform ? (
            <section className="af-card" style={{ padding: 24 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  marginBottom: 18,
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>
                    {translate('pages.admin.admin_emails_page.admin_emails_page.div.template')}
                  </div>
                  <div style={{ color: 'var(--af-muted)', fontSize: 13, marginTop: 4 }}>
                    {translate('pages.admin.admin_emails_page.admin_emails_page.div.copy')}
                  </div>
                </div>
                <div style={{ width: 300, maxWidth: '100%' }}>
                  <CustomSelect
                    value={selectedTemplateType}
                    onChange={(value) => {
                      const nextType = value as NotificationType;
                      setSelectedTemplateType(nextType);
                      setTemplateDraft(templates.find((item) => item.type === nextType) ?? null);
                      setShowSource(false);
                    }}
                    options={templates.map((template) => ({
                      value: template.type,
                      label: template.name,
                    }))}
                  />
                </div>
              </div>

              {templateDraft && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(180px, 280px) 1fr auto',
                      gap: 14,
                      alignItems: 'end',
                    }}
                  >
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_emails_page.admin_emails_page.label_label.copy_2',
                      )}

                      <input
                        className="af-input"
                        value={templateDraft.name}
                        onChange={(e) => patchDraft({ name: e.target.value })}
                      />
                    </label>
                    <label className="af-label">
                      {translate(
                        'pages.admin.admin_emails_page.admin_emails_page.label_label.subject',
                      )}

                      <input
                        className="af-input"
                        value={templateDraft.subject}
                        onChange={(e) => patchDraft({ subject: e.target.value })}
                      />
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        fontSize: 13,
                        color: 'var(--af-text)',
                        fontWeight: 700,
                        paddingBottom: 12,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={templateDraft.isEnabled}
                        onChange={(e) => patchDraft({ isEnabled: e.target.checked })}
                      />

                      {translate('pages.admin.admin_emails_page.admin_emails_page.label.active')}
                    </label>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 0.9fr)',
                      gap: 18,
                      alignItems: 'start',
                    }}
                  >
                    <div style={{ display: 'grid', gap: 14 }}>
                      <div
                        style={{
                          border: '1px solid var(--af-border)',
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: 'var(--af-s1)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                            padding: 10,
                            borderBottom: '1px solid var(--af-border)',
                            background: 'var(--af-s2)',
                          }}
                        >
                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label',
                            )}
                            onClick={() => runEditorCommand('bold')}
                          />

                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_2',
                            )}
                            onClick={() => runEditorCommand('italic')}
                          />

                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_3',
                            )}
                            onClick={() => runEditorCommand('formatBlock', 'h2')}
                          />

                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_4',
                            )}
                            onClick={() => runEditorCommand('formatBlock', 'p')}
                          />

                          <EditorButton
                            label={'•'}
                            onClick={() => runEditorCommand('insertUnorderedList')}
                          />

                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_5',
                            )}
                            active={showSource}
                            onClick={() => {
                              if (!showSource) syncEditorHtml();
                              setShowSource((current) => !current);
                            }}
                          />

                          <div style={{ width: 220, maxWidth: '100%' }}>
                            <CustomSelect
                              value=""
                              placeholder={translate(
                                'pages.admin.admin_emails_page.admin_emails_page.custom_select.placeholder_content',
                              )}
                              onChange={(value) => {
                                if (value) insertPlaceholder(value);
                              }}
                              options={placeholders.map((item) => ({ value: item, label: item }))}
                            />
                          </div>
                        </div>
                        {showSource ? (
                          <textarea
                            className="af-input af-mono"
                            style={{
                              border: 0,
                              borderRadius: 0,
                              minHeight: 280,
                              resize: 'vertical',
                            }}
                            value={templateDraft.htmlBody}
                            onChange={(e) => patchDraft({ htmlBody: e.target.value })}
                          />
                        ) : (
                          <div
                            ref={editorRef}
                            contentEditable
                            suppressContentEditableWarning
                            onInput={syncEditorHtml}
                            style={{
                              minHeight: 280,
                              padding: 22,
                              color: 'var(--af-text)',
                              outline: 'none',
                              lineHeight: 1.65,
                              background: 'var(--af-s0)',
                            }}
                          />
                        )}
                      </div>

                      <label className="af-label">
                        {translate(
                          'pages.admin.admin_emails_page.admin_emails_page.label_label.copy_3',
                        )}

                        <textarea
                          className="af-input"
                          rows={4}
                          value={templateDraft.textBody}
                          onChange={(e) => patchDraft({ textBody: e.target.value })}
                        />
                      </label>
                      <label className="af-label">
                        {translate(
                          'pages.admin.admin_emails_page.admin_emails_page.label_label.sms',
                        )}

                        <textarea
                          className="af-input"
                          rows={3}
                          value={templateDraft.smsBody ?? ''}
                          onChange={(e) => patchDraft({ smsBody: e.target.value })}
                        />
                      </label>
                    </div>

                    <aside style={{ display: 'grid', gap: 12, position: 'sticky', top: 18 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800 }}>
                            {translate(
                              'pages.admin.admin_emails_page.admin_emails_page.div.preview',
                            )}
                          </div>
                          <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 2 }}>
                            {translate(
                              'pages.admin.admin_emails_page.admin_emails_page.div.copy_2',
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_6',
                            )}
                            active={previewMode === 'desktop'}
                            onClick={() => setPreviewMode('desktop')}
                          />

                          <EditorButton
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.editor_button.label_7',
                            )}
                            active={previewMode === 'mobile'}
                            onClick={() => setPreviewMode('mobile')}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          border: '1px solid var(--af-border)',
                          borderRadius: 8,
                          background: 'var(--af-s2)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            padding: '12px 14px',
                            borderBottom: '1px solid var(--af-border)',
                            background: 'var(--af-s1)',
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--af-dim)',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: 4,
                            }}
                          >
                            {translate(
                              'pages.admin.admin_emails_page.admin_emails_page.div.subject',
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: 'var(--af-text)',
                              fontWeight: 700,
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {previewSubject}
                          </div>
                        </div>
                        <div
                          style={{
                            padding: previewMode === 'mobile' ? '16px 8px' : 18,
                            background: '#d8d8d2',
                          }}
                        >
                          <iframe
                            title={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.iframe.email',
                              {
                                value1: templateDraft.name,
                              },
                            )}
                            srcDoc={previewHtml}
                            style={{
                              display: 'block',
                              width: '100%',
                              maxWidth: previewMode === 'mobile' ? 320 : 600,
                              height: previewMode === 'mobile' ? 760 : 860,
                              margin: '0 auto',
                              background: '#ffffff',
                              border: 0,
                              borderRadius: 8,
                              boxShadow: '0 18px 50px rgba(0,0,0,0.16)',
                            }}
                          />
                        </div>
                      </div>

                      <details
                        style={{
                          border: '1px solid var(--af-border)',
                          borderRadius: 8,
                          padding: 12,
                          background: 'var(--af-s1)',
                        }}
                      >
                        <summary
                          style={{
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 800,
                            color: 'var(--af-text)',
                          }}
                        >
                          {translate('pages.admin.admin_emails_page.admin_emails_page.summary.sms')}
                        </summary>
                        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                          <PreviewTextBlock
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.preview_text_block.label',
                            )}
                            value={previewText}
                          />

                          <PreviewTextBlock
                            label={translate(
                              'pages.admin.admin_emails_page.admin_emails_page.preview_text_block.sms',
                            )}
                            value={
                              previewSms ||
                              translate(
                                'pages.admin.admin_emails_page.admin_emails_page.preview_text_block.value',
                              )
                            }
                          />
                        </div>
                      </details>
                    </aside>
                  </div>

                  <button
                    className="af-primary-btn"
                    style={{ padding: '12px 18px', fontSize: 13, justifySelf: 'start' }}
                    disabled={saving}
                    onClick={() => void saveTemplate()}
                  >
                    {saving ? (
                      <Spinner />
                    ) : (
                      translate(
                        'pages.admin.admin_emails_page.admin_emails_page.button_primary_btn.save_2',
                      )
                    )}
                  </button>
                </div>
              )}
            </section>
          ) : (
            <section
              className="af-card"
              style={{ padding: 24, color: 'var(--af-muted)', fontSize: 13 }}
            >
              {translate('pages.admin.admin_emails_page.admin_emails_page.section_card.email')}
            </section>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function PreviewTextBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--af-dim)',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        className="af-mono"
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          color: 'var(--af-muted)',
          fontSize: 12,
          lineHeight: 1.6,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function EditorButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'af-primary-btn' : 'af-outline-btn'}
      style={{
        minWidth: 38,
        height: 34,
        padding: '0 10px',
        fontSize: 12,
        justifyContent: 'center',
      }}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
